// Copyright 2026 AllSpice. All rights reserved. Not Gitea.
// SPDX-License-Identifier: UNLICENSED

// The component table DRCY posts in its review summary, as JSON inside a
// `drcy-component-table` code block. The checker owns the shape
// (connections_checker/review/component_table.py); this module reads it,
// searches and filters it, and flattens it for download.

import {serializeCsv} from './csv.ts';
import {resolvePageName} from '../components/smart-review-utils.ts';

export const COMPONENT_TABLE_SCHEMA_VERSION = 1;

export type Severity = 'error' | 'warning' | 'recommendation';
export type ComponentStatus = 'issues' | 'no_issues' | 'incomplete' | 'failed';

// `name` is the page name as the design stores it, possibly empty, and `file`
// its schematic file; `pageLabel` turns them into what the page selector shows.
export type TablePage = {key: string; name: string; file: string; order: number; url: string | null};

export type TableEvidence = {statement: string; label: string; url: string | null};

export type TableAssertion = {
  observed_condition: string;
  requirement: string;
  consequence: string;
  evidence: TableEvidence[];
};

export type TableLocation = {designator: string; pins: string[]};

// Each error group posts one comment; group ids are unique within a page.
export type TableGroup = {id: string; label: string; root: boolean};

// The comment DRCY would have posted for a finding the severity threshold
// kept back, ready for a reader to post as themselves.
export type TableComment = {
  path: string;
  sub_path: string | null;
  body: string;
  severity: Severity;
  finding_ids: string[];
};

export type TableFinding = {
  page_key: string;
  finding_id: string;
  severity: Severity;
  headline: string;
  // This component's pins and the other parts the finding names, which its
  // comment's callout lists together.
  pins: string[];
  other_locations: TableLocation[];
  // `unverified` when no verification ran, which is the default.
  verification: 'verified' | 'unverified' | 'incomplete';
  // Whether the finding has a comment on the schematic and, if not, why.
  posting: 'posted' | 'below_threshold' | 'file_not_in_review' | 'empty';
  // Posted resolved: every finding in its comment matches dismissed feedback.
  auto_resolved: boolean;
  // The name of the hidden anchor the finding's posted comment carries.
  comment_anchor: string | null;
  unposted_comment: TableComment | null;
  group: TableGroup | null;
  // What the posted comment says in full; the table shows only the headline.
  assertions: TableAssertion[];
  repair: string | null;
  repair_notices: string[];
  verification_failure: string | null;
};

export type TableCheck = {
  page_key: string;
  check_id: string;
  subject: string;
  question: string;
  missing_evidence: string;
};

export type TableAnalysis = {
  page_key: string;
  summary: string | null;
  assumptions: string[];
  concerns: string[];
  note: string | null;
  // Why the component's review failed on this page.
  failure: string | null;
};

// Where DRCY got the datasheet, such as DigiKey; tables from a checker that
// did not send it have none.
export type TableDatasheet = {state: 'used' | 'unavailable' | 'not_used'; url: string | null; source?: string | null};

export type TablePartNumber = {
  value: string;
  part_type: string | null;
  provenance: string;
  upload_url: string | null;
};

export type TableNotice = {kind: 'repo_datasheet_failed' | 'missing_part_number'; message: string; url: string | null};

export type ComponentRow = {
  designator: string;
  symbol_id: string | null;
  name: string;
  pages: TablePage[];
  status: ComponentStatus;
  severity: Severity | null;
  issue_count: number;
  analyses: TableAnalysis[];
  findings: TableFinding[];
  checks: TableCheck[];
  notices: TableNotice[];
  datasheet: TableDatasheet | null;
  part_number: TablePartNumber | null;
  // The comment a click on the row opens: its most serious posted finding's.
  comment_anchor: string | null;
};

export type ComponentTable = {schema_version: number; rows: ComponentRow[]};

export const STATUS_LABELS: Record<ComponentStatus, string> = {
  issues: 'Issues found',
  no_issues: 'No issues',
  incomplete: 'Not accounted for',
  failed: 'Review failed',
};

export const SEVERITY_LABELS: Record<Severity, string> = {
  error: 'Error',
  warning: 'Warning',
  recommendation: 'Recommendation',
};

// Most severe first, as the severity filter lists them.
export const SEVERITIES: readonly Severity[] = ['error', 'warning', 'recommendation'];

// Sort ranks put what needs attention first when sorting descending.
export const STATUS_RANK: Record<ComponentStatus, number> = {no_issues: 0, incomplete: 1, failed: 2, issues: 3};
export const SEVERITY_RANK: Record<Severity, number> = {recommendation: 1, warning: 2, error: 3};

export const STATUSES: readonly ComponentStatus[] = ['issues', 'no_issues', 'incomplete', 'failed'];
const VERIFICATIONS: readonly TableFinding['verification'][] = ['verified', 'unverified', 'incomplete'];
const POSTINGS: readonly TableFinding['posting'][] = ['posted', 'below_threshold', 'file_not_in_review', 'empty'];
const DATASHEET_STATES: readonly TableDatasheet['state'][] = ['used', 'unavailable', 'not_used'];
const NOTICE_KINDS: readonly TableNotice['kind'][] = ['repo_datasheet_failed', 'missing_part_number'];

// The block is markdown anyone could have written, and the server's sanitizer
// never looks inside its JSON, so every shape the table or the download
// dereferences is checked here: a row that fails renders the "incomplete"
// message with its source, not a half-drawn table that throws.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

function isArrayOf<T>(value: unknown, isItem: (item: unknown) => item is T): value is T[] {
  return Array.isArray(value) && value.every(isItem);
}

// A closed set, checked without `in`: that would accept any property name an
// object inherits, "toString" included.
function oneOf<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (values as readonly string[]).includes(value);
}

// Links are not checked for shape here; `webUrl` decides what becomes a link.
function isPage(value: unknown): value is TablePage {
  return isRecord(value) && isString(value.key) && isString(value.name) && isString(value.file) && isNumber(value.order);
}

function isEvidence(value: unknown): value is TableEvidence {
  return isRecord(value) && isString(value.statement) && isString(value.label);
}

function isAssertion(value: unknown): value is TableAssertion {
  return isRecord(value) && isString(value.observed_condition) && isString(value.requirement) &&
    isString(value.consequence) && isArrayOf(value.evidence, isEvidence);
}

function isLocation(value: unknown): value is TableLocation {
  return isRecord(value) && isString(value.designator) && isStringArray(value.pins);
}

function isComment(value: unknown): value is TableComment {
  return isRecord(value) && isString(value.path) && isNullableString(value.sub_path) && isString(value.body) &&
    oneOf(SEVERITIES, value.severity) && isStringArray(value.finding_ids);
}

function isGroup(value: unknown): value is TableGroup {
  return isRecord(value) && isString(value.id) && isString(value.label) && isBoolean(value.root);
}

function isFinding(value: unknown): value is TableFinding {
  return isRecord(value) && isString(value.page_key) && isString(value.finding_id) && oneOf(SEVERITIES, value.severity) &&
    isString(value.headline) && isStringArray(value.pins) && isArrayOf(value.other_locations, isLocation) &&
    oneOf(VERIFICATIONS, value.verification) && oneOf(POSTINGS, value.posting) && isBoolean(value.auto_resolved) &&
    isNullableString(value.comment_anchor) && (value.unposted_comment === null || isComment(value.unposted_comment)) &&
    (value.group === null || isGroup(value.group)) &&
    isArrayOf(value.assertions, isAssertion) && isNullableString(value.repair) && isStringArray(value.repair_notices) &&
    isNullableString(value.verification_failure);
}

function isCheck(value: unknown): value is TableCheck {
  return isRecord(value) && isString(value.page_key) && isString(value.check_id) && isString(value.subject) &&
    isString(value.question) && isString(value.missing_evidence);
}

function isAnalysis(value: unknown): value is TableAnalysis {
  return isRecord(value) && isString(value.page_key) && isNullableString(value.summary) && isStringArray(value.assumptions) &&
    isStringArray(value.concerns) && isNullableString(value.note) && isNullableString(value.failure);
}

function isDatasheet(value: unknown): value is TableDatasheet {
  return isRecord(value) && oneOf(DATASHEET_STATES, value.state) && (value.source === undefined || isNullableString(value.source));
}

function isPartNumber(value: unknown): value is TablePartNumber {
  return isRecord(value) && isString(value.value) && isNullableString(value.part_type) && isString(value.provenance);
}

function isNotice(value: unknown): value is TableNotice {
  return isRecord(value) && oneOf(NOTICE_KINDS, value.kind) && isString(value.message);
}

function isRow(value: unknown): value is ComponentRow {
  return isRecord(value) && isString(value.designator) && isNullableString(value.symbol_id) && isString(value.name) &&
    isArrayOf(value.pages, isPage) && oneOf(STATUSES, value.status) &&
    (value.severity === null || oneOf(SEVERITIES, value.severity)) && isNumber(value.issue_count) &&
    isArrayOf(value.analyses, isAnalysis) && isArrayOf(value.findings, isFinding) &&
    isArrayOf(value.checks, isCheck) && isArrayOf(value.notices, isNotice) &&
    (value.datasheet === null || isDatasheet(value.datasheet)) &&
    (value.part_number === null || isPartNumber(value.part_number)) && isNullableString(value.comment_anchor);
}

// A link the table may render: a web address, or a Hub path. Anything else, a
// `javascript:` address above all, becomes no link at all, and the row stays.
function webUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const {protocol} = new URL(value, 'https://hub.invalid/');
    return protocol === 'http:' || protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}

function readRow(row: ComponentRow): ComponentRow {
  return {
    ...row,
    pages: row.pages.map((page) => ({...page, url: webUrl(page.url)})),
    findings: row.findings.map((finding) => ({
      ...finding,
      assertions: finding.assertions.map((assertion) => ({
        ...assertion,
        evidence: assertion.evidence.map((item) => ({...item, url: webUrl(item.url)})),
      })),
    })),
    notices: row.notices.map((notice) => ({...notice, url: webUrl(notice.url)})),
    datasheet: row.datasheet && {...row.datasheet, url: webUrl(row.datasheet.url)},
    part_number: row.part_number && {...row.part_number, upload_url: webUrl(row.part_number.upload_url)},
  };
}

// Throws with a message a reader can act on: the block renders as an error in
// place of the table, and the raw JSON stays below it.
export function parseComponentTable(source: string): ComponentTable {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch {
    throw new Error('The DRCY component table could not be read.');
  }
  if (!isRecord(raw) || raw.schema_version !== COMPONENT_TABLE_SCHEMA_VERSION) {
    throw new Error('This DRCY component table was written in a format this version of AllSpice Hub cannot show.');
  }
  const rows: unknown = raw.rows;
  if (!isArrayOf(rows, isRow)) {
    throw new Error('The DRCY component table is incomplete.');
  }
  return {schema_version: COMPONENT_TABLE_SCHEMA_VERSION, rows: rows.map(readRow)};
}

// The checker gzips the table and writes it as base64, which keeps a large
// board's review about a ninth of the size; a block of plain JSON is read as is.
export async function decodeComponentTable(source: string): Promise<ComponentTable> {
  const text = source.trim();
  if (text.startsWith('{')) return parseComponentTable(text);
  let json: string;
  try {
    const bytes = Uint8Array.from(atob(text.replace(/\s+/g, '')), (char) => char.charCodeAt(0));
    const stream = new ReadableStream({
      start(controller) {
        controller.enqueue(bytes);
        controller.close();
      },
    });
    json = await new Response(stream.pipeThrough(new DecompressionStream('gzip'))).text();
  } catch {
    throw new Error('The DRCY component table could not be read.');
  }
  return parseComponentTable(json);
}

// The label Request DRCY's page selector gives the same page, so the two never
// disagree on a name.
export function pageLabel(page: TablePage): string {
  return resolvePageName(page.name, page.file);
}

// The comment a click on the row goes to: its first finding that was posted.
export function rowCommentAnchor(row: ComponentRow): string | null {
  return row.comment_anchor;
}

// Every page any row is drawn on, in schematic order across the review.
export function tablePages(rows: ComponentRow[]): TablePage[] {
  const pages = new Map<string, TablePage>();
  for (const row of rows) {
    for (const page of row.pages) if (!pages.has(page.key)) pages.set(page.key, page);
  }
  return Array.from(pages.values()).sort((a, b) => a.order - b.order);
}

// A row matches a page filter when it is drawn on any selected page, so a part
// split across two pages shows up under either. No selection matches all.
export function rowOnPages(row: ComponentRow, pageKeys: ReadonlySet<string>): boolean {
  return pageKeys.size === 0 || row.pages.some((page) => pageKeys.has(page.key));
}

function analysisParts(row: ComponentRow): string[] {
  const parts: string[] = [];
  for (const analysis of row.analyses) {
    if (analysis.summary) parts.push(analysis.summary);
    parts.push(...analysis.assumptions, ...analysis.concerns);
    if (analysis.note) parts.push(analysis.note);
    if (analysis.failure) parts.push(analysis.failure);
  }
  return parts;
}

// Everything a search can match: what identifies the part, where it is, and
// what the review said about it.
export function rowSearchText(row: ComponentRow): string {
  return [
    row.designator,
    row.name,
    row.part_number?.value ?? '',
    ...row.pages.map(pageLabel),
    ...analysisParts(row),
    ...row.findings.map((finding) => finding.headline),
    ...row.checks.flatMap((check) => [check.subject, check.question]),
  ].join('\n').toLowerCase();
}

// A row matches a severity filter when any of its findings has a selected
// severity, so a part with an error and a warning shows under either. No
// selection matches all.
export function rowHasSeverity(row: ComponentRow, severities: ReadonlySet<Severity>): boolean {
  return severities.size === 0 || row.findings.some((finding) => severities.has(finding.severity));
}

// An issue is what one comment reports: a group, or a finding outside any.
// The same key the checker counts a row's issues by.
function issueKey(finding: TableFinding): string {
  const issue = finding.group ? `group:${finding.group.id}` : `finding:${finding.finding_id}`;
  return `${finding.page_key}\u0000${issue}`;
}

// The first of the most severe, so ties keep the order the checker wrote.
function mostSevere(findings: TableFinding[]): TableFinding | null {
  let worst: TableFinding | null = null;
  for (const finding of findings) {
    if (worst === null || SEVERITY_RANK[finding.severity] > SEVERITY_RANK[worst.severity]) worst = finding;
  }
  return worst;
}

// The row as the severity filter shows it: only the findings of a chosen
// severity, with the row's severity, issue count and comment worked out from
// those alone, so a part with an error and a warning shows only its error
// under "Errors". No selection shows the row as it is.
export function rowWithSeverities(row: ComponentRow, severities: ReadonlySet<Severity>): ComponentRow {
  if (severities.size === 0) return row;
  const findings = row.findings.filter((finding) => severities.has(finding.severity));
  const commented = findings.filter((finding) => finding.comment_anchor !== null && finding.posting === 'posted');
  return {
    ...row,
    findings,
    severity: mostSevere(findings)?.severity ?? null,
    issue_count: new Set(findings.map(issueKey)).size,
    comment_anchor: mostSevere(commented)?.comment_anchor ?? null,
  };
}

export function rowMatchesSearch(searchText: string, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  return terms.every((term) => searchText.includes(term));
}

// Whether a row's analysis names the page of each part: it does for a part
// drawn on several, so one page's account is not read as the whole.
export function rowLabelsPages(row: ComponentRow): boolean {
  return row.pages.length > 1;
}

// The analysis text the Analysis column sorts by.
export function analysisText(row: ComponentRow): string {
  return analysisParts(row).join('\n');
}

// Natural order for text, the same for every viewer: digit runs compare as
// numbers, so R9 comes before R10, and the rest by code point, with no locale
// rules or case folding. Splitting on digit runs puts a number only at odd
// positions, so a number is never compared with text. The checker orders its
// rows the same way.
function textRuns(text: string): (string | number)[] {
  return text.split(/(\d+)/).map((run, index) => (index % 2 ? Number(run) : run));
}

export function compareText(a: string, b: string): number {
  const runsA = textRuns(a);
  const runsB = textRuns(b);
  for (let index = 0; index < Math.min(runsA.length, runsB.length); index++) {
    const runA = runsA[index];
    const runB = runsB[index];
    if (runA < runB) return -1;
    if (runA > runB) return 1;
  }
  return runsA.length - runsB.length;
}

// Where a long library name may wrap: after the separators KiCad and Altium
// names use, so "Battery_Management:BQ24075RGT" breaks at "_" and ":" rather
// than mid-word.
export function nameBreaks(name: string): string[] {
  return name.split(/(?<=[:_/])/);
}

export function pinsLabel(pins: readonly string[]): string {
  if (pins.length === 0) return '';
  return `${pins.length === 1 ? 'Pin' : 'Pins'} ${pins.join(', ')}`;
}

export function partNumberText(part: TablePartNumber): string {
  const type = part.part_type ? ` (${part.part_type})` : '';
  return `${part.value}${type}, ${part.provenance}`;
}

// The download is the review's findings, one line each and the most serious
// first, with the part each is about on its own line, so the file filters and
// sorts cleanly in a spreadsheet. Parts without findings stay in the table.
// No cell holds a line break: a cell that runs over several lines reads as a
// broken file in an editor and makes the spreadsheet row as tall as the
// comment.
export const CSV_HEADER = [
  'Severity', 'Designator', 'Part number', 'Page', 'Where', 'Finding', 'Requirement', 'Consequence',
  'Suggested repair', 'Evidence', 'Comment', 'Comment link', 'Schematic link', 'Datasheet', 'Datasheet source',
] as const;

type CsvColumn = typeof CSV_HEADER[number];

// What a spreadsheet would not keep as typed: a formula (= + - @, which net
// names such as +5V and -12V start), a leading apostrophe it would hide, and
// whole cells it would read as a number or date and rewrite: leading zeros
// (part number 0402), digit strings it shortens to scientific notation,
// decimals it trims (1.500), scientific notation (1E10), dates (a pin or page
// named 1-2) and times.
const SPREADSHEET_REWRITES = /^(?:[=+\-@'].*|0\d+|\d{12,}|\d+\.\d+|\d+(?:\.\d+)?e[+-]?\d+|\d{1,4}[-/.]\d{1,2}(?:[-/.]\d{1,4})?|\d{1,2}:\d{2}(?::\d{2})?)$/i;

// Excel caps a string literal inside a formula at 255 characters and a whole
// formula at 8192, both counted in UTF-16 units.
const EXCEL_LITERAL_LIMIT = 255;
const EXCEL_FORMULA_LIMIT = 8192;

function isHighSurrogate(code: number): boolean {
  return code >= 0xD800 && code <= 0xDBFF;
}

// Pieces of at most one literal, never cut between the two halves of a
// character: the UTF-8 download would turn a lone half into U+FFFD.
function literalPieces(text: string): string[] {
  const pieces: string[] = [];
  for (let start = 0; start < text.length;) {
    let end = Math.min(start + EXCEL_LITERAL_LIMIT, text.length);
    if (end < text.length && isHighSurrogate(text.charCodeAt(end - 1))) end--;
    pieces.push(text.slice(start, end));
    start = end;
  }
  return pieces;
}

// Kept as typed in Excel and Google Sheets: a formula whose only term is a
// string literal. A leading apostrophe marks text only when typed into a cell;
// read from a file, Excel and LibreOffice show it, so it is the fallback only
// for text too long for any formula.
function asTyped(line: string): string {
  const formula = `=${literalPieces(line).map((piece) => `"${piece.replace(/"/g, '""')}"`).join('&')}`;
  return formula.length > EXCEL_FORMULA_LIMIT ? `'${line}` : formula;
}

// One line, and kept as typed where a spreadsheet would rewrite it.
function csvCell(text: string): string {
  const line = text.replace(/\s+/g, ' ').trim();
  return SPREADSHEET_REWRITES.test(line) ? asTyped(line) : line;
}

// Page labels for the file, with the schematic file added where two pages
// would otherwise read the same, as two projects' Power sheets do.
function pageNamer(rows: ComponentRow[]): (page: TablePage | undefined) => string {
  const keysByLabel = new Map<string, Set<string>>();
  for (const page of tablePages(rows)) {
    const label = pageLabel(page);
    keysByLabel.set(label, (keysByLabel.get(label) ?? new Set()).add(page.key));
  }
  return (page) => {
    if (!page) return '';
    const label = pageLabel(page);
    return (keysByLabel.get(label)?.size ?? 0) > 1 ? `${label} (${page.file})` : label;
  };
}

type PageNamer = ReturnType<typeof pageNamer>;

function pageOf(row: ComponentRow, pageKey: string): TablePage | undefined {
  return row.pages.find((page) => page.key === pageKey);
}

function locationText(location: TableLocation): string {
  if (location.pins.length === 0) return location.designator;
  return `${location.designator} ${location.pins.length === 1 ? 'pin' : 'pins'} ${location.pins.join(', ')}`;
}

function evidenceText(evidence: TableEvidence[], separator: string): string {
  return evidence.map((item) => `${item.statement} (${item.url ? `${item.label}, ${item.url}` : item.label})`).join(separator);
}

// A finding whose verification is incomplete keeps each original assertion,
// as its comment does; on its one line they are numbered, and the numbered
// parts are what " | " separates.
function assertionsText(finding: TableFinding, part: (assertion: TableAssertion, numbered: boolean) => string): string {
  const numbered = finding.assertions.length > 1;
  if (!numbered) return finding.assertions.map((assertion) => part(assertion, false)).join('');
  return finding.assertions.map((assertion, index) => `(${index + 1}) ${part(assertion, true)}`).join(' | ');
}

// Blank when no verification ran, which is the default: the comment says
// nothing about it either.
function verificationText(finding: TableFinding): string {
  switch (finding.verification) {
    case 'verified': return 'Verified';
    case 'incomplete': return `Verification incomplete: ${finding.verification_failure ?? ''}`;
    case 'unverified': return '';
  }
}

// As of posting; DRCY resolves its own comment when every finding in it
// matches a comment a reviewer dismissed before.
export const POSTING_NOTES: Record<Exclude<TableFinding['posting'], 'posted'>, string> = {
  below_threshold: 'Not Posted; Below severity threshold',
  file_not_in_review: 'Not Posted; File not part of review',
  empty: 'Not Posted',
};

function commentText(finding: TableFinding): string {
  if (finding.posting !== 'posted') return POSTING_NOTES[finding.posting];
  return finding.auto_resolved ? 'Posted and resolved by DRCY: matches a previously dismissed comment' : 'Posted';
}

type GroupMember = {designators: string[]; findingId: string; root: boolean};

// Every member of each error group, by page and group id, root first as the
// comment lists them, so each member's line can name the whole comment.
function groupMembers(rows: ComponentRow[]): Map<string, GroupMember[]> {
  const groups = new Map<string, GroupMember[]>();
  for (const row of rows) {
    for (const finding of row.findings) {
      if (!finding.group) continue;
      const key = `${finding.page_key}\u0000${finding.group.id}`;
      const members = groups.get(key) ?? [];
      if (!members.some((member) => member.findingId === finding.finding_id)) {
        const designators = [row.designator, ...finding.other_locations.map((location) => location.designator)];
        members.push({designators, findingId: finding.finding_id, root: finding.group.root});
      }
      groups.set(key, members);
    }
  }
  for (const members of groups.values()) members.sort((a, b) => Number(b.root) - Number(a.root));
  return groups;
}

// "R187 and R188", or "C5, C6 and C7".
function listText(items: string[]): string {
  return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

// The other parts whose findings DRCY wrote into the same comment as this one.
function sharedCommentText(row: ComponentRow, finding: TableFinding, groups: Map<string, GroupMember[]>): string {
  if (!finding.group) return '';
  const members = groups.get(`${finding.page_key}\u0000${finding.group.id}`) ?? [];
  const others = new Set(members.flatMap((member) => member.designators));
  others.delete(row.designator);
  return others.size ? `one comment with ${listText(Array.from(others).sort(compareText))}` : '';
}

type FileContext = {
  groups: Map<string, GroupMember[]>;
  pageName: PageNamer;
  commentLink: (anchor: string) => string;
};

function csvLine(cells: Partial<Record<CsvColumn, string>>): string[] {
  return CSV_HEADER.map((column) => csvCell(cells[column] ?? ''));
}

// Pins first, then the other parts the finding names: "Pins B5, B6; also J8
// pins 14, 16".
function whereText(finding: TableFinding): string {
  const others = finding.other_locations.map(locationText).join('; ');
  if (!others) return pinsLabel(finding.pins);
  return finding.pins.length ? `${pinsLabel(finding.pins)}; also ${others}` : `Also ${others}`;
}

function repairText(finding: TableFinding): string {
  const notes = finding.repair_notices.join('; ');
  if (!finding.repair) return notes;
  return notes ? `${finding.repair} (${notes})` : finding.repair;
}

function findingLine(row: ComponentRow, finding: TableFinding, context: FileContext): string[] {
  const page = pageOf(row, finding.page_key);
  const part = row.part_number;
  const datasheet = row.datasheet?.state === 'used' ? row.datasheet : null;
  return csvLine({
    'Severity': SEVERITY_LABELS[finding.severity],
    'Designator': row.designator,
    'Part number': part ? `${part.value}${part.part_type ? ` (${part.part_type})` : ''}` : '',
    'Page': context.pageName(page),
    'Where': whereText(finding),
    'Finding': assertionsText(finding, (assertion) => assertion.observed_condition) || finding.headline,
    'Requirement': assertionsText(finding, (assertion) => assertion.requirement),
    'Consequence': assertionsText(finding, (assertion) => assertion.consequence),
    'Suggested repair': repairText(finding),
    'Evidence': assertionsText(finding, (assertion, numbered) => evidenceText(assertion.evidence, numbered ? '; ' : ' | ')),
    'Comment': [commentText(finding), sharedCommentText(row, finding, context.groups), verificationText(finding)].filter(Boolean).join('; '),
    'Comment link': finding.comment_anchor ? context.commentLink(finding.comment_anchor) : '',
    'Schematic link': page?.url ?? '',
    'Datasheet': datasheet?.url ?? '',
    'Datasheet source': datasheet?.source ?? '',
  });
}

export type CsvOptions = {
  // The whole table, for naming every member of a group and telling pages
  // apart; `rows` may be only the rows a filter shows.
  table?: ComponentRow[];
  // A link to the posted comment that carries this anchor, or '' if none.
  commentLink?: (anchor: string) => string;
};

// The findings of these rows, in CRLF lines as RFC 4180 and Excel expect.
export function findingsCsv(rows: ComponentRow[], {table = rows, commentLink = () => ''}: CsvOptions = {}): string {
  const context: FileContext = {groups: groupMembers(table), pageName: pageNamer(table), commentLink};
  const lines = rows.flatMap((row) => row.findings.map((finding) => ({row, finding})));
  // Most serious first; within a severity, the order the table shows.
  lines.sort((a, b) => SEVERITY_RANK[b.finding.severity] - SEVERITY_RANK[a.finding.severity]);
  return serializeCsv({
    header: Array.from(CSV_HEADER),
    rows: lines.map(({row, finding}) => findingLine(row, finding, context)),
    newline: '\r\n',
    trailingNewline: true,
  });
}
